import client from '../apollo/client';
import * as queries from '../graphql/queries';
import * as mutations from '../graphql/mutations';

export const graphqlService = {
  // User methods
  getAllUsers: async () => {
    try {
      const { data, error } = await client.query({ query: queries.GET_USERS });
      if (error) {
        console.error('GraphQL error in getAllUsers:', error);
        throw error;
      }
      if (!data || !data.users) {
        console.warn('No users data returned from GraphQL');
        return [];
      }
      console.log('getAllUsers - Received users:', data.users.length, 'users');
      if (data.users.length > 0) {
        console.log('First user sample:', { name: data.users[0].name, email: data.users[0].email });
      }
      return data.users || [];
    } catch (error) {
      console.error('Error in getAllUsers:', error);
      throw error;
    }
  },
  
  getUser: async (id) => {
    const { data } = await client.query({ 
      query: queries.GET_USER, 
      variables: { id } 
    });
    return data.user;
  },
  
  getUserByEmail: async (email) => {
    const { data } = await client.query({ 
      query: queries.GET_USER_BY_EMAIL, 
      variables: { email } 
    });
    return data.userByEmail;
  },
  
  getUsersByRole: async (role) => {
    const { data } = await client.query({ 
      query: queries.GET_USERS_BY_ROLE, 
      variables: { role } 
    });
    return data.usersByRole;
  },
  
  getUsersByDepartment: async (department) => {
    const { data } = await client.query({ 
      query: queries.GET_USERS_BY_DEPARTMENT, 
      variables: { department } 
    });
    return data.usersByDepartment;
  },
  
  searchUsers: async (query) => {
    const { data } = await client.query({ 
      query: queries.SEARCH_USERS, 
      variables: { query } 
    });
    return data.searchUsers;
  },
  
  getUserStats: async () => {
    const { data } = await client.query({ query: queries.GET_USER_STATS });
    return data.userStats;
  },
  
  createUser: async (input) => {
    const { data } = await client.mutate({ 
      mutation: mutations.CREATE_USER, 
      variables: { input } 
    });
    return data.createUser;
  },
  
  updateUser: async (id, input) => {
    const { data } = await client.mutate({ 
      mutation: mutations.UPDATE_USER, 
      variables: { id, input } 
    });
    return data.updateUser;
  },
  
  deleteUser: async (id) => {
    const { data } = await client.mutate({ 
      mutation: mutations.DELETE_USER, 
      variables: { id } 
    });
    return data.deleteUser;
  },
  
  // Module methods
  getAllModules: async () => {
    try {
      const { data, error } = await client.query({ query: queries.GET_MODULES });
      if (error) {
        console.error('GraphQL error in getAllModules:', error);
        throw error;
      }
      if (!data || !data.modules) {
        console.warn('No modules data returned from GraphQL');
        return [];
      }
      return data.modules || [];
    } catch (error) {
      console.error('Error in getAllModules:', error);
      throw error;
    }
  },
  
  getModule: async (id) => {
    const { data } = await client.query({ 
      query: queries.GET_MODULE, 
      variables: { id } 
    });
    return data.module;
  },
  
  getModulesByDifficulty: async (difficulty) => {
    const { data } = await client.query({ 
      query: queries.GET_MODULES_BY_DIFFICULTY, 
      variables: { difficulty } 
    });
    return data.modulesByDifficulty;
  },
  
  searchModules: async (query) => {
    const { data } = await client.query({ 
      query: queries.SEARCH_MODULES, 
      variables: { query } 
    });
    return data.searchModules;
  },
  
  getModulesForUser: async (user) => {
    // Filter user object to only include fields defined in UserInput
    const userInput = {
      email: user?.email || null,
      role: user?.role || null,
      department: user?.department || null,
      designation: user?.designation || null,
      specialization: user?.specialization || null
    };
    
    console.log('graphqlService.getModulesForUser - Requesting modules for user:', userInput);
    
    const { data } = await client.query({ 
      query: queries.GET_MODULES_FOR_USER, 
      variables: { user: userInput } 
    });
    
    console.log('graphqlService.getModulesForUser - Received modules from backend:', {
      count: data?.modulesForUser?.length || 0,
      modules: data?.modulesForUser?.map(m => ({ id: m.id, title: m.title })) || []
    });
    
    return data.modulesForUser;
  },
  
  getModuleStats: async () => {
    const { data } = await client.query({ query: queries.GET_MODULE_STATS });
    return data.moduleStats;
  },
  
  createModule: async (input) => {
    const { data } = await client.mutate({ 
      mutation: mutations.CREATE_MODULE, 
      variables: { input } 
    });
    return data.createModule;
  },
  
  updateModule: async (id, input) => {
    const { data } = await client.mutate({ 
      mutation: mutations.UPDATE_MODULE, 
      variables: { id, input } 
    });
    return data.updateModule;
  },
  
  deleteModule: async (id) => {
    const { data } = await client.mutate({ 
      mutation: mutations.DELETE_MODULE, 
      variables: { id } 
    });
    return data.deleteModule;
  },
  
  // Notification methods
  getNotifications: async (userEmail) => {
    const { data } = await client.query({ 
      query: queries.GET_NOTIFICATIONS, 
      variables: { userEmail } 
    });
    return data.notifications;
  },
  
  getUnreadCount: async (userEmail) => {
    const { data } = await client.query({ 
      query: queries.GET_UNREAD_COUNT, 
      variables: { userEmail } 
    });
    return data.unreadCount;
  },
  
  createNotification: async (input) => {
    const { data } = await client.mutate({ 
      mutation: mutations.CREATE_NOTIFICATION, 
      variables: { input } 
    });
    return data.createNotification;
  },
  
  markNotificationAsRead: async (id) => {
    const { data } = await client.mutate({ 
      mutation: mutations.MARK_NOTIFICATION_AS_READ, 
      variables: { id } 
    });
    return data.markNotificationAsRead;
  },
  
  markAllNotificationsAsRead: async (userEmail) => {
    const { data } = await client.mutate({ 
      mutation: mutations.MARK_ALL_NOTIFICATIONS_AS_READ, 
      variables: { userEmail } 
    });
    return data.markAllNotificationsAsRead;
  },
  
  deleteNotification: async (id) => {
    const { data } = await client.mutate({ 
      mutation: mutations.DELETE_NOTIFICATION, 
      variables: { id } 
    });
    return data.deleteNotification;
  },
  
  clearAllNotifications: async (userEmail) => {
    const { data } = await client.mutate({ 
      mutation: mutations.CLEAR_ALL_NOTIFICATIONS, 
      variables: { userEmail } 
    });
    return data.clearAllNotifications;
  },
  
  // Progress methods
  getProgress: async (userEmail) => {
    const { data } = await client.query({ 
      query: queries.GET_PROGRESS, 
      variables: { userEmail } 
    });
    return data.progress;
  },
  
  getProgressByModule: async (userEmail, moduleId) => {
    const { data } = await client.query({ 
      query: queries.GET_PROGRESS_BY_MODULE, 
      variables: { userEmail, moduleId } 
    });
    return data.progressByModule;
  },
  
  getProgressStats: async (userEmail) => {
    const { data } = await client.query({ 
      query: queries.GET_PROGRESS_STATS, 
      variables: { userEmail } 
    });
    return data.progressStats;
  },
  
  createOrUpdateProgress: async (input) => {
    const { data } = await client.mutate({ 
      mutation: mutations.CREATE_OR_UPDATE_PROGRESS, 
      variables: { input } 
    });
    return data.createOrUpdateProgress;
  },
  
  updateProgress: async (userEmail, moduleId, input) => {
    const { data } = await client.mutate({ 
      mutation: mutations.UPDATE_PROGRESS, 
      variables: { userEmail, moduleId, input } 
    });
    return data.updateProgress;
  },
  
  // Auth methods
  login: async (email, password) => {
    const { data } = await client.mutate({ 
      mutation: mutations.LOGIN, 
      variables: { input: { email, password } } 
    });
    return data.login;
  },
  
  verifyAuth: async (email) => {
    const { data } = await client.query({ 
      query: queries.VERIFY_AUTH, 
      variables: { email } 
    });
    return data.verifyAuth;
  },

  // Schedule methods
  getAllSchedules: async () => {
    const { data } = await client.query({ query: queries.GET_SCHEDULES });
    return data.schedules;
  },
  
  getSchedule: async (id) => {
    const { data } = await client.query({ 
      query: queries.GET_SCHEDULE, 
      variables: { id } 
    });
    return data.schedule;
  },
  
  getSchedulesByModule: async (moduleId) => {
    const { data } = await client.query({ 
      query: queries.GET_SCHEDULES_BY_MODULE, 
      variables: { moduleId } 
    });
    return data.schedulesByModule;
  },
  
  createSchedule: async (input) => {
    const { data } = await client.mutate({ 
      mutation: mutations.CREATE_SCHEDULE, 
      variables: { input } 
    });
    return data.createSchedule;
  },
  
  updateSchedule: async (id, input) => {
    const { data } = await client.mutate({ 
      mutation: mutations.UPDATE_SCHEDULE, 
      variables: { id, input } 
    });
    return data.updateSchedule;
  },
  
  deleteSchedule: async (id) => {
    const { data } = await client.mutate({ 
      mutation: mutations.DELETE_SCHEDULE, 
      variables: { id } 
    });
    return data.deleteSchedule;
  },
  
  // Prescreening methods
  getPrescreening: async (userEmail) => {
    const { data } = await client.query({ 
      query: queries.GET_PRESCREENING, 
      variables: { userEmail } 
    });
    return data.prescreening;
  },
  
  createOrUpdatePrescreening: async (input) => {
    // Filter out MongoDB-specific fields that shouldn't be in the input
    // Only include fields that are defined in PrescreeningInput type
    const cleanInput = {
      userEmail: input.userEmail,
      ...(input.category !== undefined && { category: input.category }),
      ...(input.level !== undefined && { level: input.level }),
      ...(input.profession !== undefined && { profession: input.profession }),
      ...(input.goal !== undefined && { goal: input.goal }),
      ...(input.tags !== undefined && { tags: input.tags }),
      ...(input.completed !== undefined && { completed: input.completed }),
      ...(input.skipped !== undefined && { skipped: input.skipped })
    };
    
    const { data } = await client.mutate({ 
      mutation: mutations.CREATE_OR_UPDATE_PRESCREENING, 
      variables: { input: cleanInput } 
    });
    return data.createOrUpdatePrescreening;
  },
  
  deletePrescreening: async (userEmail) => {
    const { data } = await client.mutate({ 
      mutation: mutations.DELETE_PRESCREENING, 
      variables: { userEmail } 
    });
    return data.deletePrescreening;
  },
  
  // Settings methods
  getAllSettings: async () => {
    const { data } = await client.query({ query: queries.GET_SETTINGS });
    return data.settings;
  },
  
  getSetting: async (key) => {
    const { data } = await client.query({ 
      query: queries.GET_SETTING, 
      variables: { key } 
    });
    return data.setting;
  },
  
  getSettingsByCategory: async (category) => {
    const { data } = await client.query({ 
      query: queries.GET_SETTINGS_BY_CATEGORY, 
      variables: { category } 
    });
    return data.settingsByCategory;
  },
  
  createOrUpdateSetting: async (input) => {
    const { data } = await client.mutate({ 
      mutation: mutations.CREATE_OR_UPDATE_SETTING, 
      variables: { input } 
    });
    return data.createOrUpdateSetting;
  },
  
  deleteSetting: async (key) => {
    const { data } = await client.mutate({ 
      mutation: mutations.DELETE_SETTING, 
      variables: { key } 
    });
    return data.deleteSetting;
  },

  // Tag methods
  getAllTags: async () => {
    const { data } = await client.query({ query: queries.GET_TAGS });
    return data.tags;
  },
  
  getTag: async (name) => {
    const { data } = await client.query({ 
      query: queries.GET_TAG, 
      variables: { name } 
    });
    return data.tag;
  },
  
  getTagsByCategory: async (category) => {
    const { data } = await client.query({ 
      query: queries.GET_TAGS_BY_CATEGORY, 
      variables: { category } 
    });
    return data.tagsByCategory;
  },
  
  getActiveTags: async () => {
    const { data } = await client.query({ query: queries.GET_ACTIVE_TAGS });
    return data.activeTags;
  },
  
  createTag: async (input) => {
    const { data } = await client.mutate({ 
      mutation: mutations.CREATE_TAG, 
      variables: { input } 
    });
    return data.createTag;
  },
  
  updateTag: async (name, input) => {
    const { data } = await client.mutate({ 
      mutation: mutations.UPDATE_TAG, 
      variables: { name, input } 
    });
    return data.updateTag;
  },
  
  deleteTag: async (name) => {
    const { data } = await client.mutate({ 
      mutation: mutations.DELETE_TAG, 
      variables: { name } 
    });
    return data.deleteTag;
  },

  // Role methods
  getAllRoles: async () => {
    const { data } = await client.query({ query: queries.GET_ROLES });
    return data.roles;
  },
  
  getRole: async (name) => {
    const { data } = await client.query({ 
      query: queries.GET_ROLE, 
      variables: { name } 
    });
    return data.role;
  },
  
  getActiveRoles: async () => {
    const { data } = await client.query({ query: queries.GET_ACTIVE_ROLES });
    return data.activeRoles;
  },
  
  createRole: async (input) => {
    const { data } = await client.mutate({ 
      mutation: mutations.CREATE_ROLE, 
      variables: { input } 
    });
    return data.createRole;
  },
  
  updateRole: async (name, input) => {
    const { data } = await client.mutate({ 
      mutation: mutations.UPDATE_ROLE, 
      variables: { name, input } 
    });
    return data.updateRole;
  },
  
  deleteRole: async (name) => {
    const { data } = await client.mutate({ 
      mutation: mutations.DELETE_ROLE, 
      variables: { name } 
    });
    return data.deleteRole;
  }
};

